import { IconX } from "@tabler/icons-react";
import React, { useEffect, useState } from "react";
import SelectInput from "../../../components/Select";
import { useCityUpdateMutation, useFetchCountriesAllData, useFetchCountryHubsAllData } from "../../../apis/locationApis";
import toast from "react-hot-toast";

interface City {
  id: string;
  name: string;
  arabicName: string;
  cityCode: string;
  countryId: string;
  hubId: string;
  country: string;
  hub: string;
}

type Props = {
  open: boolean;
  onClose: () => void;
  city: City;
  setCity:  React.Dispatch<React.SetStateAction<City>>;
  fetchData: () => void;
};

const CitiesEditViewModal = ({ open, onClose, city, setCity, fetchData }: Props) => {
  const [editMode, setEditMode] = useState(false);
  const { data: countries } = useFetchCountriesAllData();
  const { data: hubs, refetch } = useFetchCountryHubsAllData(city.countryId);
  const { mutate, isPending } = useCityUpdateMutation();

  useEffect(() => {
    if (city.countryId) {
      refetch();
    }
  }, [city.countryId]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setCity({ ...city, [e.target.name]: e.target.value });
  };

  const toggleMode = () => {
    if (!editMode) {
      setEditMode(!editMode)
    }

    mutate(
      { ...city, type: "'lmClient'" },
      {
        onSuccess: (data) => {
          onClose()
          fetchData();
        },
        onError: (error) => {
          toast.error(`${error}`);
        },
      }
    );
  };

  if (!open) return null;

  return (
    <div className="fixed top-0 left-0 w-full h-screen bg-black/60 z-50 flex items-center justify-center">
      <div className="bg-[#F7F6F2] rounded-2xl w-[655px]">
        <div className="flex items-center justify-between px-4 py-4 border-b border-[#d2d2d2]">
          <h1 className="text-2xl">{editMode ? "Edit City" : "View City"}</h1>
          <button
            onClick={() => {
              if (editMode) setEditMode(false);
              else onClose();
            }}
            className="bg-[#F2F2F7] rounded-full p-1 cursor-pointer"
          >
            <IconX size={20} />
          </button>
        </div>

        <div className="w-full p-4 divide-y divide-[#D2D2D2] space-y-3">
          <div className="flex justify-between items-center">
            <label className="w-1/2 text-sm font-medium">Country</label>
            {editMode ? (
              <SelectInput
                name="countryId"
                label=""
                placeholder="Select Country"
                selectOptions={
                  countries?.map((country: any) => ({
                    id: country?.id,
                    value: country?.id,
                    text: country?.name,
                  })) || []
                }
                value={city.countryId}
                className="w-1/2"
                setValue={(val: string) => setCity({ ...city, countryId: val })}
              />
            ) : (
              <div className="w-1/2 text-[#1E293B] mt-1">{city.country}</div>
            )}
          </div>

          {['name', 'arabicName', 'cityCode'].map((key) => (
            <div key={key} className="w-full flex justify-between items-center gap-2">
              <label className="w-1/2 capitalize text-[#212121] text-sm font-medium">
                {key === 'name'
                  ? 'City Name'
                  : key === 'arabicName'
                  ? 'City Arabic Name'
                  : 'City Code'}
              </label>
              <div className="w-1/2 pt-2">
                {editMode ? (
                  <input
                    name={key}
                    value={city[key as keyof typeof city]}
                    onChange={handleChange}
                    className="px-4 py-2 text-sm w-full rounded-full outline-none bg-white border border-[#D8D8D8]"
                  />
                ) : (
                  <div className="text-[#1E293B]">{city[key as keyof City]}</div>
                )}
              </div>
            </div>
          ))}
        </div>

        <div className="border-t border-[#D8D8D8] px-4 py-4 flex justify-end space-x-2">
          <button
            className="border border-[#D8D8D8] bg-transparent flex items-center rounded-full p-2 px-4 space-x-1 cursor-pointer text-base font-medium"
            onClick={() => {
              if (editMode) setEditMode(false);
              else onClose();
            }}
          >
            Cancel
          </button>
          <button
            className="border border-[#FBD037] bg-[#FBD037] flex items-center rounded-full p-2 px-6 space-x-1 disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer text-sm font-medium"
            onClick={toggleMode}
            disabled={isPending}
          >
            {editMode ? "Update" : "Edit"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CitiesEditViewModal;
